import { useState, useEffect, useMemo } from 'react'
import { Modal } from '../ui/Modal'
import { useToast } from '../ui/Toast'

interface Props {
  open: boolean
  onClose: () => void
  siteName: string
  siteId: string
  nextCamNumber: number
  defaultExternalIp?: string
  onCreated: (cameras: {
    site_id: string
    name: string
    cam_number: number
    cam_ip: string
    port: number
    external_ip?: string
    rtsp_url: string
    stream_name: string
    rtsp_path: string
    username: string
    password: string
  }[]) => Promise<void>
}

export function AddCameraDialog({ open, onClose, siteName, siteId, nextCamNumber, defaultExternalIp, onCreated }: Props) {
  const { toast } = useToast()

  const [mode, setMode] = useState<'single' | 'batch'>('single')

  // Single mode
  const [name, setName] = useState('')
  const [camNumber, setCamNumber] = useState(nextCamNumber.toString())
  const [camIp, setCamIp] = useState('')
  const [camPort, setCamPort] = useState('443')
  const [externalIp, setExternalIp] = useState(defaultExternalIp ?? '')
  const [rtspPath, setRtspPath] = useState('/snl/live/1/2')
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('admin')
  const [streamName, setStreamName] = useState('')

  // Batch mode
  const [batchCount, setBatchCount] = useState('4')
  const [startIp, setStartIp] = useState('')
  const [startNumber, setStartNumber] = useState(nextCamNumber.toString())

  const [saving, setSaving] = useState(false)

  // Pre-fill external IP from existing cameras when dialog opens
  useEffect(() => {
    if (open && defaultExternalIp) {
      setExternalIp(defaultExternalIp)
    }
  }, [open, defaultExternalIp])

  // Build default stream name: SiteName (no spaces) + -cam + N
  // e.g. "Doms Office" → "DomsOffice-cam4"
  function buildStreamName(siteName: string, camNum: number) {
    const base = siteName.replace(/\s+/g, '')
    return `${base}-cam${camNum}`
  }

  // Preview for single mode
  const previewStreamName = useMemo(() => {
    if (streamName.trim()) return streamName.trim()
    return buildStreamName(siteName, parseInt(camNumber) || 1)
  }, [siteName, camNumber, streamName])

  function buildRtspUrl(ip: string, path: string, user: string, pass: string) {
    return `rtsp://${user}:${pass}@${ip}:554${path}`
  }

  function incrementIp(ip: string, offset: number): string {
    const parts = ip.split('.').map(Number)
    parts[3] += offset
    return parts.join('.')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)

    try {
      const portNum = parseInt(camPort) || 80

      if (mode === 'single') {
        const camNum = parseInt(camNumber)
        const finalStreamName = streamName.trim() || buildStreamName(siteName, camNum)
        await onCreated([{
          site_id: siteId,
          name: name.trim() || `Camera ${camNum}`,
          cam_number: camNum,
          cam_ip: camIp.trim(),
          port: portNum,
          ...(externalIp.trim() ? { external_ip: externalIp.trim() } : {}),
          rtsp_url: buildRtspUrl(camIp.trim(), rtspPath, username, password),
          stream_name: finalStreamName,
          rtsp_path: rtspPath,
          username,
          password,
        }])
        toast('Camera added', 'success')
      } else {
        const count = parseInt(batchCount) || 1
        const startNum = parseInt(startNumber)
        const cameras = Array.from({ length: count }, (_, i) => {
          const camNum = startNum + i
          const ip = incrementIp(startIp.trim(), i)
          return {
            site_id: siteId,
            name: `Camera ${camNum}`,
            cam_number: camNum,
            cam_ip: ip,
            port: portNum,
            ...(externalIp.trim() ? { external_ip: externalIp.trim() } : {}),
            rtsp_url: buildRtspUrl(ip, rtspPath, username, password),
            stream_name: buildStreamName(siteName, camNum),
            rtsp_path: rtspPath,
            username,
            password,
          }
        })
        await onCreated(cameras)
        toast(`${count} cameras added`, 'success')
      }

      onClose()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to add camera', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-xs uppercase tracking-wide text-gray-500 mb-1.5'

  return (
    <Modal open={open} onClose={onClose} title="Add Camera">
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => setMode('single')}
          className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
            mode === 'single' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
          }`}
        >
          Single
        </button>
        <button
          type="button"
          onClick={() => setMode('batch')}
          className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
            mode === 'batch' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
          }`}
        >
          Batch
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'single' ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Front Gate"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Cam #</label>
                <input
                  type="number"
                  value={camNumber}
                  onChange={(e) => setCamNumber(e.target.value)}
                  min={1}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>External IP *</label>
              <input
                type="text"
                value={externalIp}
                onChange={(e) => setExternalIp(e.target.value)}
                placeholder="e.g. 81.133.159.99"
                className={inputCls}
              />
              <p className="text-[10px] text-gray-600 mt-1">Public IP where the camera can be reached from the internet</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Camera LAN IP</label>
                <input
                  type="text"
                  value={camIp}
                  onChange={(e) => setCamIp(e.target.value)}
                  placeholder="e.g. 192.168.0.121"
                  className={inputCls}
                />
                <p className="text-[10px] text-gray-600 mt-1">Private IP on the camera's local network</p>
              </div>
              <div>
                <label className={labelCls}>External Port</label>
                <input
                  type="number"
                  value={camPort}
                  onChange={(e) => setCamPort(e.target.value)}
                  placeholder="443"
                  className={inputCls}
                />
                <p className="text-[10px] text-gray-600 mt-1">Port the camera is reachable on (e.g. 443 for HTTPS)</p>
              </div>
            </div>

            {(
              <div>
                <label className={labelCls}>Relay Stream Key</label>
                <input
                  type="text"
                  value={streamName}
                  onChange={(e) => setStreamName(e.target.value)}
                  placeholder={buildStreamName(siteName, parseInt(camNumber) || 1)}
                  className={inputCls}
                />
                <p className="text-[10px] text-gray-600 mt-1">
                  Unique key used inside the relay. Leave blank to auto-generate. Preview: <span className="text-gray-400 font-mono">{previewStreamName}</span>
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Count *</label>
                <input
                  type="number"
                  value={batchCount}
                  onChange={(e) => setBatchCount(e.target.value)}
                  min={1}
                  max={20}
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Start #</label>
                <input
                  type="number"
                  value={startNumber}
                  onChange={(e) => setStartNumber(e.target.value)}
                  min={1}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>External Port</label>
                <input
                  type="number"
                  value={camPort}
                  onChange={(e) => setCamPort(e.target.value)}
                  placeholder="443"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>External IP</label>
              <input
                type="text"
                value={externalIp}
                onChange={(e) => setExternalIp(e.target.value)}
                placeholder="e.g. 81.133.159.99"
                className={inputCls}
              />
              <p className="text-[10px] text-gray-600 mt-1">Public IP (shared by all cameras in batch)</p>
            </div>
            <div>
              <label className={labelCls}>Starting LAN IP</label>
              <input
                type="text"
                value={startIp}
                onChange={(e) => setStartIp(e.target.value)}
                placeholder="e.g. 192.168.0.121 (increments last octet)"
                className={inputCls}
              />
              <p className="text-[10px] text-gray-600 mt-1">Private IP on the camera's local network</p>
            </div>
            {(
              <div className="bg-gray-50 rounded-md px-3 py-2">
                <p className="text-[10px] text-gray-500">
                  Streams: {Array.from({ length: Math.min(parseInt(batchCount) || 1, 5) }, (_, i) => (
                    <span key={i} className="text-gray-400 font-mono">
                      {buildStreamName(siteName, (parseInt(startNumber) || 1) + i)}
                      {i < Math.min((parseInt(batchCount) || 1) - 1, 4) ? ', ' : ''}
                    </span>
                  ))}
                  {(parseInt(batchCount) || 0) > 5 && <span className="text-gray-600">...</span>}
                </p>
              </div>
            )}
          </>
        )}

        <div>
          <label className={labelCls}>RTSP Path</label>
          <input
            type="text"
            value={rtspPath}
            onChange={(e) => setRtspPath(e.target.value)}
            className={inputCls}
          />
          <p className="text-[10px] text-gray-600 mt-1">/snl/live/1/2 = sub stream (SD), /snl/live/1/1 = main stream (HD)</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Adding...' : mode === 'single' ? 'Add Camera' : `Add ${batchCount} Cameras`}
          </button>
        </div>
      </form>
    </Modal>
  )
}
